import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    Container,
    Box,
    Typography,
    CircularProgress,
    Button,
    Paper
} from '@mui/material';
import contentService from '../services/contentService';
import ContentCard from './ContentCard';
import { useAuth } from '../context/AuthContext';
import { shareContentLink } from '../utils/shareUtils';
import '../styles/ProtectedRoutes.css';

const ContentFeed = () => {
    const [content, setContent] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(true);
     const [loadError, setLoadError] = useState(false);
    const [offset, setOffset] = useState(0);
    const { user } = useAuth();
    const navigate = useNavigate();
    const observerTarget = useRef(null);

    const loadContent = async (reset = false) => {
        try {
            setLoading(true);
            setLoadError(false);
            const currentOffset = reset ? 0 : offset;
            const response = await contentService.getContentFeed(5, currentOffset);

            if (response.success) {
                if (reset) {
                    setContent(response.data);
                } else {
                    setContent(prev => [...prev, ...response.data]);
                }
                setHasMore(response.pagination.hasMore);
                setOffset(currentOffset + response.data.length);
            } else {
                toast.error('Failed to load content');
                setLoadError(true); // stop the observer from retrying automatically
            }
        } catch (error) {
            console.error('Load content error:', error);
            toast.error(error.message || 'Failed to load content');
            setLoadError(true); // stop the observer from retrying automatically
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let isMounted = true;

        const loadInitialContent = async () => {
            if (isMounted) {
                await loadContent(true);
            }
        };

        loadInitialContent();

        return () => {
            isMounted = false;
        };
    }, []); // Only run once on mount

    // Infinite scroll observer
    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && hasMore && !loading && !loadError) {
                    loadContent(false);
                }
            },
            { threshold: 0.1 }
        );

        if (observerTarget.current) {
            observer.observe(observerTarget.current);
        }

        return () => {
            if (observerTarget.current) {
                observer.unobserve(observerTarget.current);
            }
        };
    }, [hasMore, loading, loadError, offset]);

    const handleLike = useCallback(async (contentId) => {
        if (!user || user.role !== 'Customer') {
            toast.error('Please login as a customer to like posts');
            return;
        }

        try {
            const response = await contentService.toggleLike(contentId);
            if (response.success) {
                // Update the content state with new like status
                setContent(prev => prev.map(item =>
                    item.ContentId === contentId
                        ? {
                            ...item,
                            LikeCount: response.data.likeCount,
                            IsLikedByUser: response.data.isLiked ? 1 : 0
                        }
                        : item
                ));
            } else {
                toast.error(response.message || 'Failed to like post');
            }
        } catch (error) {
            console.error('Like error:', error);
            toast.error(error.message || 'Failed to like post');
        }
    }, [user]);

    const handleComment = useCallback(async (contentId, commentText) => {
        if (!user || user.role !== 'Customer') {
            toast.error('Please login as a customer to comment');
            return;
        }

        try {
            const response = await contentService.addComment(contentId, commentText);
            if (response.success) {
                // Update comment count
                setContent(prev => prev.map(item =>
                    item.ContentId === contentId
                        ? { ...item, CommentCount: parseInt(item.CommentCount) + 1 }
                        : item
                ));
                toast.success('Comment added successfully');
                return true;
            } else {
                toast.error(response.message || 'Failed to add comment');
                return false;
            }
        } catch (error) {
            console.error('Comment error:', error);
            toast.error(error.message || 'Failed to add comment');
            return false;
        }
    }, [user]);

    const handleShare = useCallback(async (contentId) => {
        if (!user || user.role !== 'Customer') {
            toast.error('Please login as a customer to share posts');
            return;
        }

        try {
            const response = await contentService.shareContent(contentId, 'link');
            if (response.success) {
                const shareResult = await shareContentLink(contentId, {
                    title: 'Check this post',
                    text: 'Take a look at this post on Shipway'
                });

                setContent(prev => prev.map(item =>
                    item.ContentId === contentId
                        ? { ...item, ShareCount: response.data.shareCount }
                        : item
                ));
            } else {
                toast.error(response.message || 'Failed to share post');
            }
        } catch (error) {
            console.error('Share error:', error);
            toast.error(error.message || 'Failed to share post');
        }
    }, [user]);

    const handleDelete = (contentId) => {
        // Remove the deleted post from the feed
        setContent(prevContent => prevContent.filter(item => item.ContentId !== contentId));
    };

    const loadMore = () => {
        if (!loading && hasMore) {
            loadContent(false);
        }
    };

    const handleUploaderClick = (uploaderId) => {
        // Navigate to uploader's profile
        navigate(`/profile/${uploaderId}`);
    };

    if (loading && content.length === 0) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Box display="flex" flexDirection="column" alignItems="center" gap={2} className="glass-content-container">
                    <CircularProgress size={40} className="glass-loading" />
                    <Typography variant="body1" className="glass-text-secondary">
                        Loading content...
                    </Typography>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 2 }}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                alignItems: 'center', // Center the cards
                width: '100%'
            }}>
                {content.map((item) => (
                    <Box
                        key={item.ContentId}
                        sx={{
                            width: '100%',
                            maxWidth: {
                                xs: '100%',      // Mobile S (320px): full width
                                sm: '100%',      // Mobile M (375px): full width  
                                md: '100%',      // Mobile L (425px): full width
                                lg: '425px',     // Tablet (768px): fixed to Mobile L width
                                xl: '425px'      // Laptop+ (1024px+): fixed to Mobile L width
                            },
                            '@media (min-width: 769px)': {
                                maxWidth: '425px' // Force Mobile L width for tablet and above
                            }
                        }}
                    >
                        <ContentCard
                            content={item}
                            onLike={handleLike}
                            onComment={handleComment}
                            onShare={handleShare}
                            currentUser={user}
                            onUploaderClick={handleUploaderClick}
                            onDelete={handleDelete}
                        />
                    </Box>
                ))}
            </Box>

            {/* Infinite scroll trigger */}
            {/* Infinite scroll trigger */}
            {hasMore && (
                <Box
                    ref={observerTarget}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mt: 3,
                        minHeight: '100px',
                        gap: 1.5
                    }}
                >
                    {loading && <CircularProgress size={30} className="glass-loading" />}

                    {loadError && !loading && (
                        <>
                            <Typography variant="body2" className="glass-text-secondary">
                                Couldn't load more posts. Check your connection.
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => loadContent(false)}
                                sx={{
                                    color: '#bc965e',
                                    borderColor: 'rgba(188, 150, 94, 0.5)',
                                    '&:hover': { borderColor: '#bc965e', backgroundColor: 'rgba(188, 150, 94, 0.1)' }
                                }}
                            >
                                Retry
                            </Button>
                        </>
                    )}
                </Box>
            )}

            {!hasMore && content.length > 0 && (
                <Box sx={{ textAlign: 'center', mt: 3 }} className="glass-content-container">
                    <Typography variant="body2" className="glass-text-secondary">
                        You've seen all the posts!
                    </Typography>
                </Box>
            )}

            {content.length === 0 && !loading && (
                <Paper elevation={1} className="glass-empty-state">
                    <Typography variant="h6" className="glass-text-secondary" gutterBottom>
                        No content available yet
                    </Typography>
                    <Typography variant="body2" className="glass-text-secondary">
                        Check back later for new posts!
                    </Typography>
                </Paper>
            )}
        </Container>
    );
};

export default ContentFeed;